/**
 * Verify + parse Stripe webhook signatures into canonical Almadar
 * events. Apps/builder calls this from its `/billing/stripe-webhook`
 * route and switches on the returned discriminated union — it never
 * imports the `stripe` SDK or any `Stripe.*` shape.
 *
 * The five handled event types mirror docs §5.2 / `Almadar_Studio_Subscriptions.md`:
 *   - customer.subscription.created
 *   - customer.subscription.updated
 *   - customer.subscription.deleted
 *   - invoice.payment_succeeded
 *   - invoice.payment_failed
 *
 * Anything else returns `{ error: 'unknown-event-type', stripeType: … }`
 * so the route can acknowledge with 200 + a log line (Stripe stops
 * retrying acknowledged events).
 */

import Stripe from 'stripe';
import type {
  AlmadarStripeEvent,
  AlmadarSubscription,
  AlmadarSubscriptionStatus,
  AlmadarTier,
  StripePriceMap,
} from './types';

export interface VerifyAndParseInput {
  rawBody: string | Buffer;
  signature: string;
  /** Stripe webhook secret (`whsec_…`). */
  secret: string;
  /** Map of provisioned Stripe Price IDs → Almadar tier. */
  prices: StripePriceMap;
}

const STRIPE_API_VERSION: Stripe.LatestApiVersion = '2025-02-24.acacia';

function priceToTier(priceId: string, prices: StripePriceMap): AlmadarTier | null {
  if (priceId === prices.solo) return 'solo';
  if (priceId === prices.teams) return 'teams';
  return null;
}

function mapStatus(status: Stripe.Subscription.Status): AlmadarSubscriptionStatus {
  switch (status) {
    case 'active':
    case 'past_due':
    case 'canceled':
    case 'incomplete':
    case 'incomplete_expired':
    case 'trialing':
    case 'unpaid':
      return status;
    case 'paused':
      return 'unpaid';
    default: {
      const _exhaustive: never = status;
      void _exhaustive;
      return 'incomplete';
    }
  }
}

function isoFromUnix(seconds: number): string {
  return new Date(seconds * 1000).toISOString();
}

function shapeSubscription(
  sub: Stripe.Subscription,
  prices: StripePriceMap,
): AlmadarSubscription {
  const firstItem = sub.items.data[0];
  const priceId = firstItem?.price.id ?? '';
  const quantity = firstItem?.quantity ?? 1;
  const customerId =
    typeof sub.customer === 'string' ? sub.customer : sub.customer.id;

  return {
    subscriptionId: sub.id,
    customerId,
    status: mapStatus(sub.status),
    priceId,
    quantity,
    currentPeriodStart: isoFromUnix(sub.current_period_start),
    currentPeriodEnd: isoFromUnix(sub.current_period_end),
    cancelAtPeriodEnd: sub.cancel_at_period_end,
    tier: priceToTier(priceId, prices),
  };
}

function customerIdOf(
  customer: string | Stripe.Customer | Stripe.DeletedCustomer | null,
): string {
  if (customer === null) return '';
  return typeof customer === 'string' ? customer : customer.id;
}

/**
 * Verify Stripe's signature header against the raw body and shape the
 * payload into a canonical `AlmadarStripeEvent`. The `stripe` SDK is
 * only used inside this module.
 */
export function verifyAndParseStripeEvent(
  input: VerifyAndParseInput,
): AlmadarStripeEvent {
  // Constructed lazily — `webhooks` is a stateless utility on the
  // Stripe client. We need an instance to access `constructEvent`.
  const stripe = new Stripe('placeholder-only-for-webhook-utility', {
    apiVersion: STRIPE_API_VERSION,
  });

  let event: Stripe.Event;
  try {
    event = stripe.webhooks.constructEvent(
      input.rawBody,
      input.signature,
      input.secret,
    );
  } catch {
    return { error: 'bad-signature' };
  }

  switch (event.type) {
    case 'customer.subscription.created': {
      const sub = event.data.object as Stripe.Subscription;
      return {
        type: 'subscription.created',
        eventId: event.id,
        data: shapeSubscription(sub, input.prices),
      };
    }
    case 'customer.subscription.updated': {
      const sub = event.data.object as Stripe.Subscription;
      return {
        type: 'subscription.updated',
        eventId: event.id,
        data: shapeSubscription(sub, input.prices),
      };
    }
    case 'customer.subscription.deleted': {
      const sub = event.data.object as Stripe.Subscription;
      return {
        type: 'subscription.deleted',
        eventId: event.id,
        data: {
          subscriptionId: sub.id,
          customerId: customerIdOf(sub.customer),
        },
      };
    }
    case 'invoice.payment_succeeded': {
      const invoice = event.data.object as Stripe.Invoice;
      const subRef = invoice.subscription;
      return {
        type: 'invoice.payment_succeeded',
        eventId: event.id,
        data: {
          customerId: customerIdOf(invoice.customer),
          subscriptionId:
            typeof subRef === 'string' ? subRef : subRef?.id ?? null,
          paidAt: isoFromUnix(invoice.status_transitions.paid_at ?? invoice.created),
          amountUsd: invoice.amount_paid / 100,
        },
      };
    }
    case 'invoice.payment_failed': {
      const invoice = event.data.object as Stripe.Invoice;
      const subRef = invoice.subscription;
      return {
        type: 'invoice.payment_failed',
        eventId: event.id,
        data: {
          customerId: customerIdOf(invoice.customer),
          subscriptionId:
            typeof subRef === 'string' ? subRef : subRef?.id ?? null,
          failedAt: isoFromUnix(invoice.created),
          reason:
            invoice.last_finalization_error?.message ??
            'Payment failed; reason not provided by Stripe.',
        },
      };
    }
    default:
      return { error: 'unknown-event-type', stripeType: event.type };
  }
}
